/**
 * Versioned estimate-upload helpers.
 *
 * Versions live in Snowflake (MEDIA_PLAN_DB.PUBLIC.EST_VERSION + EST_VERSION_ENTRY)
 * and are loaded once at app boot into an in-memory cache. Read helpers
 * (`listVersions`, `getLatestVersion`, `getVersionById`) stay synchronous
 * and consult the cache so existing call sites in MediaPlanBuilder do not
 * have to become async. Write helpers (`saveVersion`, `uploadNewVersion`)
 * POST to the backend and refresh the cache on success.
 *
 * Plan-to-version bindings still live in localStorage. They will migrate
 * to Snowflake together with the plan save flow in a later ticket; until
 * then bindings remain per-browser.
 */

import {
  EstimateVersion,
  EstimateKind,
  RateCard,
  RATE_CARDS,
  QuarterLabel,
  PlanEstimateBinding,
  ParseResult,
} from './types';
import { parseEstimateFile } from './parser';
import { authAxios } from '../utils/session_utils';
import { createCacheHydration } from '../utils/cache-hydration';

const BINDINGS_KEY = 'planEstimateBindings';

const API_BASE = process.env.REACT_APP_BACK_END_API;

// ─── In-memory cache ─────────────────────────────────────────────────────
//
// Hydrated once at app boot via `hydrateEstimateVersions()`. After that,
// `listVersions` / `getLatestVersion` / `getVersionById` read from this
// cache synchronously. `saveVersion` mutates it on successful POST.
//
// Lifecycle (hydrate / isHydrated / retry / onChange / error tracking)
// is owned by the shared `createCacheHydration` helper — see
// utils/cache-hydration.ts. This module owns just the Map itself.

const versionCache = new Map<number, EstimateVersion>();

const hydration = createCacheHydration<EstimateVersion>({
  resourceName: 'estimate versions',
  fetch: async () => {
    const response = await authAxios({
      method: 'GET',
      url: `${API_BASE}/snowflake/estimate-versions`,
    });
    return response?.data?.versions ?? [];
  },
  apply: (versions) => {
    versionCache.clear();
    versions.forEach(v => versionCache.set(v.id, v));
  },
});

/** Hydrate the cache from Snowflake. Safe to call multiple times — shares
 *  one fetch. Called once from App.tsx before initial render. */
export const hydrateEstimateVersions = hydration.hydrate;

/** True once the initial hydrate has completed (success or failure). */
export const isHydrated = hydration.isHydrated;

/** Last hydrate error message, or null if hydrate succeeded. */
export const getHydrateError = hydration.getError;

/**
 * Retry the initial hydrate after a previous failure. Resets state and
 * re-fetches. Safe no-op if a hydrate is currently in flight or already
 * succeeded.
 */
export const retryHydrate = hydration.retry;

/**
 * Subscribe to hydrate status changes. Returns an unsubscribe function.
 * UI components use this to re-render when retryHydrate flips state from
 * error → success (or success → error if a future retry fails).
 */
export const onHydrateChange = hydration.onChange;

// ─── Versions (sync reads, async write) ──────────────────────────────────

/** All versions, newest-id first. Reads from in-memory cache. */
export function listVersions(): EstimateVersion[] {
  const arr: EstimateVersion[] = [];
  versionCache.forEach(v => arr.push(v));
  return arr.sort((a, b) => b.id - a.id);
}

/**
 * The "current-season latest" version.
 *
 * Prefers the highest primary_year (so a 26/27 row beats a 25/26 row
 * even if the 25/26 row was inserted later with a higher id, as
 * happens after the one-off 25/26 backfill script). Within a single
 * primary_year the ordering is: highest as_of_date, then highest id
 * as tiebreaker (matches AUTO_INCREMENT insertion order). Using
 * as_of_date first means that if someone backfills an older-dated
 * estimate after a newer one has been uploaded, the newer date still
 * wins.
 *
 * Use this for "what estimate is the app currently anchored to" —
 * the pill at the top of the plan view, the Download Template button
 * on the Upload tab, etc. Use `getLatestVersionByYear` when you need
 * a specific season's latest regardless of which is current.
 *
 * GENERAL CARDS ONLY. A custom agency rate card is never "what the app is
 * anchored to" — it's reached deliberately, by a plan that was created on
 * it. This matters because a card's as-of date is independent of the
 * general master's: the first WPP file carries 2026-07-20 while the general
 * master carries 2026-05-20, so a card-blind "latest" would return WPP and
 * every caller here would be wrong. The Upload tab would announce WPP as
 * the "Current Latest Version", Download Template would hand out WPP's
 * numbers, and resolvePlanVersion would stamp an unbound plan onto WPP
 * rates.
 */
export function getLatestVersion(): EstimateVersion | null {
  let latest: EstimateVersion | null = null;
  versionCache.forEach(v => {
    if (cardOf(v) !== 'general') return;
    if (!latest) { latest = v; return; }
    if (v.primaryYear > latest.primaryYear) { latest = v; return; }
    if (v.primaryYear < latest.primaryYear) return;
    // same primary_year: compare by as_of_date first, then id
    if (v.asOfDate > latest.asOfDate) { latest = v; return; }
    if (v.asOfDate < latest.asOfDate) return;
    if (v.id > latest.id) latest = v;
  });
  return latest;
}

/**
 * Latest version for a specific season (primary_year). Used by the
 * year-aware resolution path in MediaPlanBuilder: 25/26 SF plans
 * resolve via `getLatestVersionByYear(2025)`, 26/27 Snowflake/draft
 * plans via `getLatestVersionByYear(2026)`. Returns null if no
 * version exists for that year.
 *
 * Note: After the scatter feature, the default "no explicit binding"
 * resolution for SF plans uses `getLatestVersionByYearAndKind(year,
 * 'upfront')` instead — the user spec'd that the default falls back
 * to UPFRONT specifically, not "whichever is latest overall."
 */
export function getLatestVersionByYear(year: number): EstimateVersion | null {
  let latest: EstimateVersion | null = null;
  versionCache.forEach(v => {
    if (v.primaryYear !== year) return;
    if (!latest) { latest = v; return; }
    // Order: highest as_of_date, then highest id as tiebreaker.
    // (Same as getLatestVersion — protects against out-of-order backfills.)
    if (v.asOfDate > latest.asOfDate) { latest = v; return; }
    if (v.asOfDate < latest.asOfDate) return;
    if (v.id > latest.id) latest = v;
  });
  return latest;
}

/**
 * Latest version for a specific (year, kind) — the right helper for the
 * scatter feature's "default" resolution. Used for SF source plans that
 * don't yet have an explicit binding: they resolve to
 * `getLatestVersionByYearAndKind(2025, 'upfront')`.
 *
 * Also used by the pill dropdown when sorting upfront-first then by
 * version DESC within each kind.
 */
export function getLatestVersionByYearAndKind(
  year: number,
  kind: EstimateKind,
): EstimateVersion | null {
  let latest: EstimateVersion | null = null;
  versionCache.forEach(v => {
    if (v.primaryYear !== year) return;
    if (v.kind !== kind) return;
    if (!latest) { latest = v; return; }
    // Order: highest as_of_date, then highest id as tiebreaker.
    // (Same as getLatestVersion — protects against out-of-order backfills.)
    if (v.asOfDate > latest.asOfDate) { latest = v; return; }
    if (v.asOfDate < latest.asOfDate) return;
    if (v.id > latest.id) latest = v;
  });
  return latest;
}

/**
 * Normalized rate card for a version.
 *
 * Versions restored from the legacy localStorage payload have no `rateCard`
 * at all, and a hand-edited database row could carry any casing. Everything
 * that compares rate cards goes through here so 'general' is the answer in
 * every ambiguous case — which is what a pre-rate-card estimate actually is.
 */
export function cardOf(v: EstimateVersion): RateCard {
  const raw = (v.rateCard ?? '').toString().trim().toLowerCase();
  return (RATE_CARDS as readonly string[]).includes(raw) ? (raw as RateCard) : 'general';
}

/**
 * The "late quarter" a season's estimate file only gains partway through
 * the cycle: Q4 of `primaryYear + 1` — 4Q'27 for a 26/27 file.
 *
 * A normal 26/27 upload covers 4Q'26 through 3Q'27. MVMS doesn't rate 4Q'27
 * until later in the season (roughly during 4Q'26) and then ships an extended
 * file with a fifth quarter. CY Upfront and Scatter plans display that tab
 * from the start, so until the extended file lands it renders empty.
 *
 * Returns the QuarterLabel if this version carries it, null otherwise —
 * purely derived from `quarters`, nothing extra is stored.
 *
 * Deliberately keyed on (Q4, primaryYear + 1) rather than on
 * `quarters.length > 4`: the season rolls, so next cycle this same code
 * identifies 4Q'28 in a 27/28 file with no edit, and the caller gets the
 * actual label to display rather than having to work out which quarter the
 * "extra" one was.
 */
export function lateQuarterOf(v: EstimateVersion | null | undefined): QuarterLabel | null {
  if (!v || typeof v.primaryYear !== 'number') return null;
  const target = v.primaryYear + 1;
  return (v.quarters ?? []).find(q => q.quarter === 'Q4' && q.year === target) ?? null;
}

/**
 * Latest version for a specific (year, kind, rateCard) — the correct helper
 * for every "default" resolution now that agencies can have custom cards.
 *
 * Supersedes `getLatestVersionByYearAndKind` for new callers. That helper
 * ignores the rate card entirely, so on a season that has both a general and
 * a WPP upload it can return the WPP one purely because it happens to be
 * newer — putting one agency's negotiated rates on another agency's plan.
 */
export function getLatestVersionByYearKindAndCard(
  year: number,
  kind: EstimateKind,
  rateCard: RateCard,
): EstimateVersion | null {
  let latest: EstimateVersion | null = null;
  versionCache.forEach(v => {
    if (v.primaryYear !== year) return;
    if (v.kind !== kind) return;
    if (cardOf(v) !== rateCard) return;
    if (!latest) { latest = v; return; }
    // Order: highest as_of_date, then highest id as tiebreaker.
    if (v.asOfDate > latest.asOfDate) { latest = v; return; }
    if (v.asOfDate < latest.asOfDate) return;
    if (v.id > latest.id) latest = v;
  });
  return latest;
}

/**
 * Latest GENERAL version for a year, any kind — the correct last-resort
 * fallback for every default-resolution chain.
 *
 * The chains used to end in `getLatestVersionByYear(year)`, which filters on
 * year alone. That defeats the guard it was sitting behind: if a season ever
 * has a custom card but no general upfront, the "defensive" fallback hands
 * back the custom card and the caller renders one agency's negotiated rates
 * as the default. Falling back within 'general' keeps the original intent —
 * surface *something* rather than blanking every cell — without ever crossing
 * a card boundary.
 *
 * Prefers upfront over scatter, then newest as_of_date, then highest id.
 */
export function getLatestGeneralVersionByYear(year: number): EstimateVersion | null {
  return getLatestVersionByYearKindAndCard(year, 'upfront', 'general')
      ?? getLatestVersionByYearKindAndCard(year, 'scatter', 'general');
}

/**
 * Newest version of a (year, kind, rateCard) stream that actually CARRIES the
 * late quarter — see `lateQuarterOf`.
 *
 * Distinct from `getLatestVersionByYearKindAndCard`, which returns the newest
 * version full stop. Most versions for a season don't have the late quarter,
 * and the newest one frequently won't either: the 5-quarter file lands mid-
 * season, and MVMS can ship an ordinary 4-quarter refresh after it. So "newest
 * version" and "newest version that has 4Q'27" are different versions, and
 * asking the wrong question here yields null and leaves the tab blank.
 *
 * Ordering matches the sibling helper: highest as_of_date, id as tiebreaker.
 */
export function getLatestVersionWithLateQuarter(
  year: number,
  kind: EstimateKind,
  rateCard: RateCard,
): EstimateVersion | null {
  let latest: EstimateVersion | null = null;
  versionCache.forEach(v => {
    if (v.primaryYear !== year) return;
    if (v.kind !== kind) return;
    if (cardOf(v) !== rateCard) return;
    if (!lateQuarterOf(v)) return;
    if (!latest) { latest = v; return; }
    if (v.asOfDate > latest.asOfDate) { latest = v; return; }
    if (v.asOfDate < latest.asOfDate) return;
    if (v.id > latest.id) latest = v;
  });
  return latest;
}

/**
 * Same, but across both kinds of the general card — the last-resort source for
 * a late quarter. A custom rate card (WPP) never gets its own 5-quarter file
 * by decision, so its plans fall back to the general card for that one
 * quarter. Silent by design: no marker, no tooltip.
 */
export function getLatestGeneralVersionWithLateQuarter(year: number): EstimateVersion | null {
  return getLatestVersionWithLateQuarter(year, 'upfront', 'general')
      ?? getLatestVersionWithLateQuarter(year, 'scatter', 'general');
}

/** Does any estimate exist for this (year, kind, rateCard)? */
export function hasVersionForCard(
  year: number,
  kind: EstimateKind,
  rateCard: RateCard,
): boolean {
  return getLatestVersionByYearKindAndCard(year, kind, rateCard) !== null;
}

/**
 * Every version matching a primary_year, restricted to one rate card.
 *
 * Used by the pill dropdown picker on 25/26 SF plans to enumerate "all
 * available estimates for this plan's season." Defaults to 'general' because
 * that picker rebinds ordinary plans — offering a custom agency card there
 * would let someone silently move a non-WPP plan onto WPP rates.
 *
 * Returned in id-ascending order so the version sequence is preserved;
 * callers usually sort/group further as they render.
 */
export function listVersionsByYear(
  year: number,
  rateCard: RateCard = 'general',
): EstimateVersion[] {
  const arr: EstimateVersion[] = [];
  versionCache.forEach(v => {
    if (v.primaryYear === year && cardOf(v) === rateCard) arr.push(v);
  });
  return arr.sort((a, b) => a.id - b.id);
}

/** Lookup by id. */
export function getVersionById(id: number): EstimateVersion | null {
  return versionCache.get(id) ?? null;
}

/**
 * Display label for an estimate version (e.g. "V1", "V2").
 *
 * The number is a 1-based **sequence within the same
 * (year, kind, rateCard)**, ordered by id ascending — NOT the raw row id,
 * and NOT scoped to year alone. That way each stream has its own independent
 * sequence: a 25/26 Scatter upload reads as "V1" even if a 25/26 Upfront "V1"
 * already exists, and WPP's first upload reads as "V1" rather than continuing
 * the general master's numbering. The user spec'd both explicitly so people
 * can say "Scatter V1" or "WPP V1" without ambiguity.
 *
 * Falls back to `V<id>` if the version isn't in the cache (defensive
 * — shouldn't happen in practice since every consumer holds a cached
 * reference).
 */
export function getVersionLabel(version: EstimateVersion): string {
  // A merged (late-quarter) version isn't in the registry — its synthetic id
  // matches nothing, so the findIndex below would miss and the label would
  // render as the raw negative, e.g. "V-1000000". Label it by the version its
  // own quarters came from, which is what the user is actually bound to; the
  // one borrowed quarter is deliberately invisible in the UI.
  if (version.mergedFrom) {
    const base = versionCache.get(version.mergedFrom.baseId);
    if (base) return getVersionLabel(base);
  }
  const card = cardOf(version);
  const sameStream: EstimateVersion[] = [];
  versionCache.forEach(v => {
    if (v.primaryYear === version.primaryYear
      && v.kind === version.kind
      && cardOf(v) === card) {
      sameStream.push(v);
    }
  });
  sameStream.sort((a, b) => a.id - b.id);
  const idx = sameStream.findIndex(v => v.id === version.id);
  if (idx < 0) return `V${version.id}`;
  return `V${idx + 1}`;
}

/** Convenience wrapper: look up by id and return the display label. */
export function getVersionLabelById(id: number): string {
  const v = versionCache.get(id);
  return v ? getVersionLabel(v) : `V${id}`;
}

/**
 * Persist a parsed upload as a new version in Snowflake. Cross-version
 * validations (duplicate as-of date, older-than-latest) are enforced
 * server-side; this client also performs them up-front against the cache
 * to surface friendlier UX errors before the round-trip.
 */
export async function saveVersion(
  parsed: Extract<ParseResult, { ok: true }>['version'],
  uploadedBy?: string,
): Promise<{ ok: true; version: EstimateVersion } | { ok: false; error: string }> {
  // Soft pre-checks against the cache. The backend has the final say.
  // Both checks are scoped to (primary_year, kind, rateCard) — same scoping
  // as the backend insert handler — so upfront, scatter and each agency rate
  // card form parallel sequences within a year and don't block each other.
  //
  // The rate card matters concretely here: WPP's first file is as-of
  // 2026-03-02 while the general master is 2026-08-20. Without rateCard in
  // the scope, the older-than-latest check below would reject it.
  const parsedKind: EstimateKind = parsed.kind ?? 'upfront';
  // Through cardOf, not `?? 'general'`. saveVersion is exported and can be
  // called with a hand-built object, and `?? 'general'` only catches
  // undefined/null — '' or 'WPP' would sail through, make `sameTrack` empty,
  // and silently disable BOTH guards below.
  const parsedCard: RateCard = cardOf(parsed as EstimateVersion);
  const trackLabel = parsedCard === 'general' ? parsedKind : `${parsedCard.toUpperCase()} ${parsedKind}`;
  const sameTrack = listVersions().filter(
    v => v.primaryYear === parsed.primaryYear
      && v.kind === parsedKind
      && cardOf(v) === parsedCard,
  );
  const dup = sameTrack.find(v => v.asOfDate === parsed.asOfDate);
  if (dup) {
    return {
      ok: false,
      error: `A ${trackLabel} estimate for ${parsed.primaryYear} with as-of date ${parsed.asOfDate} already exists (${getVersionLabel(dup)}). Each upload must have a unique as-of date within its season + track.`,
    };
  }
  if (sameTrack.length) {
    // sameTrack is id-ascending from listVersions().filter; grab the last.
    const latest = sameTrack.reduce((m, v) => (v.id > m.id ? v : m));
    if (parsed.asOfDate < latest.asOfDate) {
      return {
        ok: false,
        error: `Upload's as-of date (${parsed.asOfDate}) is older than the latest ${trackLabel} version (${getVersionLabel(latest)}, as-of ${latest.asOfDate}). Cannot upload an older version within the same track.`,
      };
    }
  }

  try {
    const response = await authAxios({
      method: 'POST',
      url: `${API_BASE}/snowflake/estimate-versions`,
      data: { version: parsed, uploadedBy: uploadedBy ?? null },
    });
    const versionId: number | undefined = response?.data?.versionId;
    if (!versionId) {
      return { ok: false, error: 'Backend did not return a new version id.' };
    }

    // Re-hydrate the cache so we pick up the new row exactly as the
    // backend stored it (including server-assigned uploaded_at). Reset
    // first so the next hydrate() call actually does work (it's a no-op
    // once already hydrated) and to discard any in-flight stale promise
    // that was started before our POST landed.
    hydration.reset();
    await hydrateEstimateVersions();
    const fresh = getVersionById(versionId);
    if (!fresh) {
      return { ok: false, error: 'New version saved but not visible after refresh.' };
    }
    return { ok: true, version: fresh };
  } catch (err: any) {
    // Backend sends { error: string, code?: string } for known cases.
    const payload = err?.response?.data;
    const msg = payload?.error || err?.message || 'Failed to save version.';
    return { ok: false, error: msg };
  }
}

// ─── Plan ↔ Version bindings (still localStorage, deferred to plan-save ticket) ─

function readBindings(): Record<string, PlanEstimateBinding> {
  try {
    const raw = localStorage.getItem(BINDINGS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    /* fall through */
  }
  return {};
}

function writeBindings(b: Record<string, PlanEstimateBinding>): void {
  localStorage.setItem(BINDINGS_KEY, JSON.stringify(b));
}

/** Get the binding for a plan, or null if not stamped yet. */
export function getBinding(planName: string): PlanEstimateBinding | null {
  return readBindings()[planName] ?? null;
}

/**
 * Stamp a plan to a specific estimate version. Idempotent on (plan, id) —
 * existing binding is preserved (versions never auto-change). Use
 * `force=true` only for explicit user-initiated rebinding actions.
 */
export function stampPlan(
  planName: string,
  estimateVersionId: number,
  opts?: { force?: boolean },
): PlanEstimateBinding {
  const all = readBindings();
  const existing = all[planName];
  if (existing && !opts?.force) return existing;
  const binding: PlanEstimateBinding = {
    planName,
    estimateVersionId,
    stampedAt: new Date().toISOString(),
  };
  writeBindings({ ...all, [planName]: binding });
  return binding;
}

/**
 * Resolve a plan to its bound estimate version. If no binding exists,
 * auto-stamp the plan to the latest version (the "first view" backfill
 * behavior the user spec'd). Returns null if no versions exist yet at
 * all — in that case the MediaPlanBuilder falls back to the legacy
 * ESTIMATE_DB.
 */
export function resolvePlanVersion(planName: string): EstimateVersion | null {
  const binding = getBinding(planName);
  if (binding) {
    return getVersionById(binding.estimateVersionId);
  }
  const latest = getLatestVersion();
  if (!latest) return null;
  stampPlan(planName, latest.id);
  return latest;
}

// ─── Helper: parse + save in one call (UI convenience) ───────────────────

/**
 * Convenience for the upload UI: parse a File, run all validations,
 * persist on success. Returns the same shape as the parser/storage
 * functions so the UI can show errors uniformly.
 *
 * `kind` overrides the parser's default ('upfront'). The Upload tab
 * passes whatever the user picked in its Type radio; consumers that
 * call this without a kind get upfront, matching legacy behavior.
 *
 * `rateCard` likewise defaults to 'general' — the master estimate every
 * plan used before agency rate cards existed. The parser never stamps
 * either field from the file; both are the user's pick in the UI.
 */
export async function uploadNewVersion(
  file: File,
  uploadedBy?: string,
  kind: EstimateKind = 'upfront',
  rateCard: RateCard = 'general',
): Promise<{ ok: true; version: EstimateVersion } | { ok: false; errors: string[] }> {
  const parsed = await parseEstimateFile(file);
  if (!parsed.ok) return { ok: false, errors: parsed.errors };
  // A custom rate card only exists for upfront — there is no WPP scatter.
  // The Upload tab hides the card selector when Scatter is picked, so this
  // is belt and braces against a caller that doesn't.
  const effectiveCard: RateCard = kind === 'upfront' ? rateCard : 'general';
  // Override the parser's stamped kind + card with the UI's picks so the
  // upload sequence joins the right track.
  const versionWithKind = { ...parsed.version, kind, rateCard: effectiveCard };
  const saved = await saveVersion(versionWithKind, uploadedBy);
  if (!saved.ok) return { ok: false, errors: [saved.error] };
  return { ok: true, version: saved.version };
}
